import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, CalendarDays, Check, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useOutletContext } from 'react-router'

import { ErrorDeLaApi, consulta, pedir } from '../api/nucleo'
import type { Sitio } from '../api/tipos'
import { Aviso, Boton, Campo, Esqueleto, Tarjeta, Titulo } from '../componentes/ui'
import type { Contexto } from './Modulo'

/**
 * La agenda de reservas de una web (CLAUDE.md §33, docs/agenda.md §2): activarla con la plantilla del
 * giro del negocio —consultorio, peluquería, canchas, restaurante, hotel…— y, ya activada, lo que falta
 * para recibir reservas. Qué rubros hay, si el plan la incluye y si ya está activada lo dice el núcleo
 * (`GET /api/agenda/plantillas`); el panel no lleva su copia del catálogo.
 */

type Plantilla = {
  slug: string
  nombre: string
  grupo: string
  descripcion: string
  recursosSeLlaman: string
  recursos: Array<{ nombre: string; etiqueta: string; tipo: string; capacidad: number }>
  servicios: Array<{ nombre: string; resumen: string; tipo: string; duracionMinutos?: number }>
  preguntas: string[]
  notas: null | string
}
type Catalogo = { activada: boolean; agenda: null | number; incluida: boolean; grupos: Array<{ valor: string; etiqueta: string }>; plantillas: Plantilla[] }

const TIPO: Record<string, string> = { cita: 'Cita', franja: 'Por horas', cupo: 'Clase con plazas', mesa: 'Mesa', estancia: 'Por noches o días' }
const duracion = (min?: number) => (!min ? '' : min % 60 === 0 ? `${min / 60} h` : min > 60 ? `${Math.floor(min / 60)} h ${min % 60} min` : `${min} min`)
const sinTildes = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

export const Agenda = () => {
  const { espacio, permisos, sitio } = useOutletContext<Contexto>()
  if (!sitio) return <Aviso tono="error">Esa web no existe, o no es tuya.</Aviso>
  if (permisos.cargando) return <Esqueleto />
  if (!permisos.tiene('agenda.configurar')) {
    return (
      <>
        <Titulo rotulo={sitio.nombre}>Agenda de reservas</Titulo>
        <Aviso tono="info">La agenda la configura quien administra la cuenta. Si hace falta, pídeselo.</Aviso>
      </>
    )
  }
  return <Pantalla espacio={espacio} key={sitio.id} sitio={sitio} />
}

const Pantalla = ({ espacio, sitio }: { espacio: number; sitio: Sitio }) => {
  const { data, isPending, error } = useQuery({ queryKey: ['agenda-plantillas', sitio.id], queryFn: () => pedir<Catalogo>(`/agenda/plantillas${consulta({ sitio: sitio.id })}`) })
  const base = `/e/${espacio}/s/${sitio.id}`
  return (
    <>
      <Titulo descripcion="Citas, clases, mesas, canchas o habitaciones: tus clientes reservan desde tu web y tú lo ves todo aquí." rotulo={sitio.nombre}>
        Agenda de reservas
      </Titulo>
      {error ? (
        <Aviso tono="error">{error instanceof ErrorDeLaApi ? error.message : 'No se ha podido cargar la agenda. Recarga la página.'}</Aviso>
      ) : isPending || !data ? (
        <Esqueleto />
      ) : data.activada ? (
        <SiguientesPasos agenda={data.agenda} base={base} />
      ) : !data.incluida ? (
        <Tarjeta>
          <div className="flex flex-col gap-3">
            <p className="text-sm">La agenda de reservas va en los planes Profesional y superiores. Al subir de plan, la activas aquí con la plantilla de tu negocio en un minuto.</p>
            <div>
              <Link className="inline-flex h-9 items-center rounded-boton bg-violeta px-3.5 text-sm font-medium text-white hover:bg-violeta-2" to={`/e/${espacio}/creditos`}>
                Ver los planes
              </Link>
            </div>
          </div>
        </Tarjeta>
      ) : (
        <Asistente catalogo={data} espacio={espacio} sitio={sitio} />
      )}
    </>
  )
}

const SiguientesPasos = ({ base, agenda }: { base: string; agenda: null | number }) => (
  <div className="flex flex-col gap-5">
    <Aviso tono="ok">Tu agenda está activada. Todo lo que creó la plantilla se puede cambiar.</Aviso>
    <Tarjeta>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-display text-base font-semibold">Lo que falta para recibir reservas</h2>
        {agenda && (
          <Link className="inline-flex h-8 items-center rounded-boton border border-linea px-3 text-[13px] font-medium hover:bg-papel-2" to={`${base}/agendas/${agenda}`}>
            Horario y reglas de la agenda
          </Link>
        )}
      </div>
      <ol className="flex flex-col gap-3 text-sm">
        {[
          { a: `${base}/servicios`, titulo: 'Pon precio a tus servicios y publícalos', texto: 'Nacen en borrador y «a consultar». Un servicio en borrador no se puede reservar desde la web.' },
          { a: `${base}/recursos`, titulo: 'Revisa tus recursos', texto: 'Los nombres, la capacidad y, si alguno tiene otro horario, el suyo.' },
          { a: `${base}/grupos-de-campos`, titulo: 'Revisa lo que se pregunta al reservar', texto: 'Es el grupo «Datos de la reserva»: añade, quita o cambia preguntas como en cualquier grupo de campos.' },
          { a: `${base}/bloqueos`, titulo: 'Apunta los días cerrados', texto: 'Vacaciones, feriados o una mañana sin atender.' },
        ].map((paso, i) => (
          <li className="flex gap-3" key={paso.a}>
            <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-violeta-suave text-xs font-semibold text-violeta">{i + 1}</span>
            <span>
              <Link className="font-medium text-violeta hover:underline" to={paso.a}>
                {paso.titulo}
              </Link>
              <span className="block text-tenue">{paso.texto}</span>
            </span>
          </li>
        ))}
      </ol>
    </Tarjeta>
  </div>
)

const Asistente = ({ catalogo, espacio, sitio }: { catalogo: Catalogo; espacio: number; sitio: Sitio }) => {
  const clienteDeConsultas = useQueryClient()
  const [busqueda, setBusqueda] = useState('')
  const [elegida, setElegida] = useState<null | Plantilla>(null)
  const [nombres, setNombres] = useState<string[]>([])
  const [enviando, setEnviando] = useState(false)
  const [fallo, setFallo] = useState<null | string>(null)

  const porGrupo = useMemo(() => {
    const q = sinTildes(busqueda.trim())
    const visibles = catalogo.plantillas.filter((p) => !q || sinTildes(`${p.nombre} ${p.descripcion}`).includes(q))
    return catalogo.grupos.map((g) => ({ ...g, plantillas: visibles.filter((p) => p.grupo === g.valor) })).filter((g) => g.plantillas.length > 0)
  }, [busqueda, catalogo])

  const elegir = (p: Plantilla) => {
    setElegida(p)
    setNombres(p.recursos.map(() => ''))
    setFallo(null)
  }

  const activar = async () => {
    if (!elegida) return
    setEnviando(true)
    setFallo(null)
    try {
      await pedir('/agenda/activar', { metodo: 'POST', cuerpo: { sitio: sitio.id, plantilla: elegida.slug, nombres } })
      // Los módulos de la web cambian (el menú enseña «Agenda») y la pantalla pasa a los siguientes pasos.
      await Promise.all([clienteDeConsultas.invalidateQueries({ queryKey: ['sitios', espacio] }), clienteDeConsultas.invalidateQueries({ queryKey: ['agenda-plantillas', sitio.id] })])
    } catch (e) {
      setFallo(e instanceof ErrorDeLaApi ? e.message : 'No se ha podido activar la agenda. Vuelve a intentarlo.')
    } finally {
      setEnviando(false)
    }
  }

  if (elegida) {
    return (
      <div className="flex flex-col gap-5">
        <div>
          <Boton icono={<ArrowLeft />} onClick={() => setElegida(null)} pequeno tipo="discreto">
            Elegir otro tipo de negocio
          </Boton>
        </div>
        <Tarjeta>
          <h2 className="font-display text-lg font-semibold">{elegida.nombre}</h2>
          <p className="mt-1 text-sm text-tenue">{elegida.descripcion}</p>

          <h3 className="mt-5 mb-2 text-sm font-semibold">{elegida.recursosSeLlaman}</h3>
          <p className="mb-3 text-xs text-tenue">Escribe sus nombres de verdad, o déjalos así y cámbialos después. Puedes añadir más cuando quieras.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {elegida.recursos.map((r, i) => (
              <Campo
                ayuda={r.capacidad > 1 ? `${r.etiqueta} con capacidad para ${r.capacidad}` : r.etiqueta}
                etiqueta={`${r.etiqueta} ${i + 1}`}
                key={`${elegida.slug}-${i}`}
                maxLength={120}
                onChange={(e) => setNombres((n) => n.map((x, j) => (j === i ? e.target.value : x)))}
                placeholder={r.nombre}
                value={nombres[i] ?? ''}
              />
            ))}
          </div>

          <h3 className="mt-5 mb-2 text-sm font-semibold">Servicios de ejemplo</h3>
          <ul className="divide-y divide-linea text-sm">
            {elegida.servicios.map((s) => (
              <li className="flex flex-wrap items-baseline justify-between gap-2 py-2" key={s.nombre}>
                <span>
                  <span className="font-medium">{s.nombre}</span>
                  <span className="block text-xs text-tenue">{s.resumen}</span>
                </span>
                <span className="text-xs text-tenue">
                  {TIPO[s.tipo] ?? s.tipo}
                  {s.duracionMinutos ? ` · ${duracion(s.duracionMinutos)}` : ''}
                </span>
              </li>
            ))}
          </ul>

          <h3 className="mt-5 mb-2 text-sm font-semibold">Lo que se pregunta al reservar</h3>
          <p className="text-sm text-tenue">{['Nombre', 'correo', 'teléfono', ...elegida.preguntas.map((p) => p.charAt(0).toLowerCase() + p.slice(1))].join(', ')}.</p>
          {elegida.notas && <p className="mt-3 text-xs text-tenue">{elegida.notas}</p>}
        </Tarjeta>
        <Aviso tono="info">Los servicios se crean en borrador y «a consultar»: ponles precio y publícalos cuando estén listos. Todo lo demás también se puede cambiar después.</Aviso>
        {fallo && <Aviso tono="error">{fallo}</Aviso>}
        <div>
          <Boton cargando={enviando} icono={<Check />} onClick={activar} tipo="primario">
            Activar la agenda
          </Boton>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm">Elige el tipo de negocio que más se parezca al tuyo. Crea lo que necesitas para empezar —quién o qué se reserva, servicios de ejemplo, horarios y las preguntas típicas— y luego lo cambias a tu medida.</p>
      <label className="relative block max-w-md">
        <span className="sr-only">Buscar un tipo de negocio</span>
        <Search aria-hidden className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-tenue" />
        <input className="h-9 w-full rounded-boton border border-linea bg-papel pr-3 pl-9 text-sm" onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar: dentista, cancha, hotel…" type="search" value={busqueda} />
      </label>
      {porGrupo.length === 0 && <p className="text-sm text-tenue">Ninguno se parece. Elige el más cercano: todo se puede cambiar después.</p>}
      {porGrupo.map((g) => (
        <section key={g.valor}>
          <h2 className="mb-2 text-xs font-medium tracking-wide text-tenue uppercase">{g.etiqueta}</h2>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {g.plantillas.map((p) => (
              <button className="flex flex-col items-start gap-1 rounded-caja border border-linea bg-papel p-4 text-left transition-colors hover:border-violeta hover:bg-violeta-suave/40" key={p.slug} onClick={() => elegir(p)} type="button">
                <span className="flex items-center gap-2 font-medium">
                  <CalendarDays aria-hidden className="size-4 text-violeta" />
                  {p.nombre}
                </span>
                <span className="text-xs text-tenue">{p.descripcion}</span>
              </button>
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
